import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import AuthService from "./authService";
import { appConfig } from '../../../config/config';
import { LoggerService } from "./logger.service";
import { IHttpError } from "models/interface";

interface ICustomError extends IHttpError {
  refreshAction: Function | string;
  params?: string;
}

export class ApiService {
  private static instance?: ApiService;

  public static getInstance() {
    if (!this.instance) {
      this.instance = new ApiService();
    }
    return this.instance;
  }

  async privateClient() {
    const authService = AuthService.getInstance();
    let client = axios.create({
      baseURL: process.env.REACT_APP_API_URL as string,
      headers: {
        Authorization: `Bearer ${await authService.getAccessToken()}`,
      },
    });

    client.interceptors.request.use(
      (config) => this.onRequest(config),
      (error) => {
        return Promise.reject(new Error(error));
      }
    );

    client.interceptors.response.use(
      (response) => this.onResponse(response),
      (error) => this.onResponseError(error)
    );

    return client;
  }

  async publicClient() {
    let userToken: string | null = null;
    try {
      const authService = AuthService.getInstance();
      const accessToken = await authService.getAccessToken();
      if(accessToken){
        userToken = `Bearer ${accessToken}`;
      }
    } catch (error) {
      
    }

    const requestConfig: {baseURL: string, headers?: any} = {
      baseURL: process.env.REACT_APP_API_URL as string,
    }

    if(userToken){
      requestConfig.headers = {
        Authorization: userToken,
      };
    }
    
    let client = axios.create(requestConfig);

    client.interceptors.request.use(
      (config) => {
        return config;
      },
      (error) => {
        return Promise.reject(new Error(error));
      }
    );

    client.interceptors.response.use(
      (response) => this.onResponse(response),
      (error) => this.onResponseError(error)
    );

    return client;
  }

  private onRequest(request: InternalAxiosRequestConfig) {
    const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    if (request.method === 'get') {
      if (!request.params) {
        request = {
          ...request,
          params: {
            timezone: currentTimeZone ?? appConfig.REACT_APP_DEFAULT_TIMEZONE,
          },
        };
      } else {
        request.params = {
          ...request.params,
          timezone: currentTimeZone ?? appConfig.REACT_APP_DEFAULT_TIMEZONE,
        };
      }
    } else {
      const bodyData = request.data??{}
      request.data = {
        ...bodyData,
        timezone: currentTimeZone ?? appConfig.REACT_APP_DEFAULT_TIMEZONE,
      }
    }

    return request;
  }

  private onResponse(response: AxiosResponse) {
    return response;
  }

  private onResponseError(error: AxiosError): Promise<ICustomError> {
    this.logTheError(error);

    if (this.isAPIDown(error)) {
      return Promise.reject({
        code: 503,
        message: "Service Unavailable",
        error: error,
      });
    } else if (error.response?.status === 403) {
      return Promise.reject({
        code: 403,
        message: "Access Denied",
        error: error,
      });
    } else if (error.response?.status === 401) {
      return Promise.reject({
        code: 401,
        message: "Unauthorized",
        error: error,
      });
    }
    return Promise.reject({
      code: error.response?.status,
      message: error.message,
      error: error,
    });
  }

  private logTheError(error: AxiosError) {
    let errorCode = error.response?.status;
    let errorResponse:any = error?.response?.data;
    let errorMessage = error.message;

    if (this.isAPIDown(error)) {
      errorCode = 503;
      errorMessage = "Service Unavailable";
    }

    if (error.request?.config?.url !== 'exception/log') {
      if(navigator.onLine){
        new LoggerService().log({
          message: errorMessage,
          status_code: errorCode,
          payload: error.request,
          errors: error
        })
        .catch((error) => console.log(error))
        .finally(() => {
          if(errorCode === 401 && errorResponse?.message && 
            (errorResponse?.message.toLowerCase() === 'dose not matched with user' || errorResponse?.message.toLowerCase() === 'does not matched with user' || errorResponse?.message.toLowerCase() === 'dose not matched with user and organization' || errorResponse?.message.toLowerCase() === 'does not matched with user and organization')
            ){
            window.location.replace(`${window.location.origin}/idle-user`);
          }
        });
      }
    }

  }

  private isAPIDown(err: AxiosError) {
    return !!err.isAxiosError && !err.response;
  }
  
}